import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { resizeImage } from '@wehunt/ui'
import { useApiFetch } from '../../shared/api'

/**
 * Longest-side cap for the team avatar. This shares its numeric value with
 * mobile's kUploadMaxImageWidth, but the two don't bound the same dimension:
 * mobile's resizeImageToMaxWidth caps width only, and only when the source is
 * wider than 1024, so a tall portrait passes through mobile untouched (an
 * 800x3000 photo stays 800x3000). This bounds the longest side instead, so
 * the same 800x3000 photo comes out 273x1024 here — web's cap is the
 * stricter of the two, not an identical one, so the object is not guaranteed
 * to be the same size whichever app wrote it.
 */
const MAX_AVATAR_DIMENSION = 1024

/**
 * The team avatar's Storage object — one deterministic path per team, the same
 * one the mobile app writes. A fixed path means a re-upload from either client
 * overwrites the previous picture instead of orphaning it, and removing has a
 * single object to delete. The `.png` extension is paired with an `image/png`
 * content type, which is why the upload forces PNG re-encoding.
 */
export function teamPhotoStoragePath(groupId: string): string {
  return `groupImages/group_pictures/${groupId}.png`
}

/**
 * Set a team's picture: resize, upload the bytes to Storage, then persist the
 * resulting download URL through the API (PUT /clubhouse/groups/{id}/photo).
 *
 * The bytes go browser → Storage directly because that is how every other
 * upload on web works and the API has no upload proxy; only the URL travels
 * through the API, which verifies it addresses this team's own object before
 * writing it to the group document.
 *
 * Owner/admin — the server enforces it. Rejects if the URL could not be
 * persisted: the bytes would be in the bucket but no client would show them,
 * so the caller must be able to tell the user it did not take effect.
 */
export function useSetTeamPhoto(groupId: string) {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File): Promise<string> => {
      const blob = await resizeImage(file, {
        maxDimension: MAX_AVATAR_DIMENSION,
        outputType: 'image/png',
      })
      const objectRef = ref(getStorage(), teamPhotoStoragePath(groupId))
      await uploadBytes(objectRef, blob, { contentType: 'image/png' })
      const url = await getDownloadURL(objectRef)
      await call<void>(`/clubhouse/groups/${encodeURIComponent(groupId)}/photo`, {
        method: 'PUT',
        body: JSON.stringify({ photoUrl: url }),
      })
      return url
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['clubhouse', 'me', 'groups'] })
    },
  })
}

/**
 * Remove a team's picture (DELETE /clubhouse/groups/{id}/photo), then delete
 * the Storage object.
 *
 * Field first, object second: the document is what every client reads, so if
 * clearing it fails there is nothing to clean up — whereas deleting the object
 * first would leave the document pointing at a 404. The object delete is
 * best-effort: a team whose picture predates this path, or whose object was
 * already removed, must still be able to clear its avatar.
 */
export function useRemoveTeamPhoto(groupId: string) {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await call<void>(`/clubhouse/groups/${encodeURIComponent(groupId)}/photo`, {
        method: 'DELETE',
      })
      try {
        await deleteObject(ref(getStorage(), teamPhotoStoragePath(groupId)))
      } catch {
        // Already gone, or never written at this path. The avatar is cleared
        // either way; a leftover object costs storage, not correctness.
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['clubhouse', 'me', 'groups'] })
    },
  })
}
